import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  Rating,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ErrorFullScreenMessage from "../../components/fullScreenMessage/ErrorFullScreenMessage";
import Loader from "../../components/loader/Loader";
import PageContentWrapper from "../../components/wrappers/PageContentWrapper";
import { FEEDBACK, FETCH_USER_RATING } from "../../enums/ApiPaths";
import { FEEDBACK_SUBMIT } from "../../enums/QueryKeys";
import { useFetch } from "../../hooks/Fetching";
import usePut from "../../hooks/usePut";
import { useIsMobile } from "../../utils/Functions";

export default function FeedbackPage() {
  const { t } = useTranslation();
  const methods = useForm();
  const key = [FEEDBACK_SUBMIT];
  const { data, isLoading, error } = useFetch(FETCH_USER_RATING, null, key);
  const url = FEEDBACK;
  const post = usePut(url, key, key);
  const [rating, setRating] = useState(data?.rating ?? 0);
  const [feedback, setFeedback] = useState(data?.description ?? "");
  const [hover, setHover] = useState(-1);

  useEffect(() => {
    if (data) {
      handleSetRating(data?.rating);
      handleSetFeedback(data?.description);
    }
  }, [data, isLoading]);

  function handleSetRating(val) {
    setRating(Number(val));
  }

  function handleSetFeedback(val) {
    setFeedback(val);
  }

  async function submit() {
    const payload = {
      rating: rating,
      description: feedback,
    };

    await post.submitCallback(payload);
  }

  if (isLoading) {
    return <Loader />;
  } else if (!isLoading && error) {
    return <ErrorFullScreenMessage />;
  }

  return (
    <PageContentWrapper>
      <form onSubmit={methods.handleSubmit(submit)}>
        <Container maxWidth="md">
          <Title data={data} />

          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography component="legend" variant="h6" gutterBottom>
                  {t("ShareYourFeedbackRating")}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mt: 1,
                  }}
                >
                  <Rating
                    name="feedback-rating"
                    value={rating}
                    precision={1}
                    onChange={(event, newValue) => {
                      handleSetRating(newValue);
                    }}
                    onChangeActive={(event, newHover) => {
                      setHover(newHover);
                    }}
                    icon={<StarIcon fontSize="inherit" color="primary" />}
                    emptyIcon={<StarBorderIcon fontSize="inherit" />}
                    size="large"
                  />
                  {rating !== null && (
                    <Typography variant="body1" color="text.secondary">
                      {hover !== -1 ? hover : rating} Star
                      {hover !== -1 && hover === 1 ? "" : "s"}
                    </Typography>
                  )}
                </Box>
                <FormHelperText>
                  1 ({t("Poor")}) - 5 ({t("Excellent")})
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                variant="outlined"
                label={t("YourFeedbackLabel")}
                placeholder={t("YourFeedbackPlaceholder")}
                value={feedback}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue.length <= 5000) {
                    //Seems like the maxLength prop has been removed, so I'm defaulting to this
                    handleSetFeedback(newValue);
                  }
                }}
                InputProps={{
                  sx: {
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "primary.main",
                    },
                  },
                }}
              />
              <CharLimit maxLeng={5000} string={feedback} />
            </Grid>

            <SubmitButton rating={rating} />
          </Grid>
        </Container>
      </form>
    </PageContentWrapper>
  );
}

function SubmitButton({ rating = null }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const justify = isMobile ? "center" : "flex-end";

  return (
    <Grid item xs={12}>
      <Box sx={{ display: "flex", justifyContent: justify }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={!rating}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "1rem",
          }}
        >
          {t("SubmitFeedback")}
        </Button>
      </Box>
    </Grid>
  );
}

function Title({ data = null }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h3" component="h1" gutterBottom>
        {t("ShareYourFeedback")}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {t("ShareYourFeedbackSubtitle")}
      </Typography>
      {data && (
        <Typography variant="body2" color="text.secondary" mt={1}>
          {t("AlreadyGaveFeedbackSubtitle")}
        </Typography>
      )}
    </Box>
  );
}

export function CharLimit({ string, maxLeng }) {
  return (
    <Typography variant="caption" color={"GrayText"}>
      {String(string).length}/{maxLeng}
    </Typography>
  );
}
